import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
} from "react";
import axios from "axios";
import { GET_TRANSLATIONS } from "../config/settings";

const TranslatorContext = createContext();

export const TranslatorProvider = ({ children }) => {
  const [language, setLanguage] = useState("en");
  const [translations, setTranslations] = useState({});

  useEffect(() => {
    const loadTranslations = async (lang) => {
      // prevent refetching same language
      if (translations[lang]) return;

      try {
        const res = await axios.get(GET_TRANSLATIONS, {
          params: {
            category: "fc",
            language: lang,
          },
        });

        setTranslations((prev) => ({
          ...prev,
          [lang]: res.data || {},
        }));
      } catch (err) {
        console.error(`[Translator] Failed to load ${lang}`, err);
        // mark as loaded to avoid infinite retries
        setTranslations((prev) => ({ ...prev, [lang]: {} }));
      }
    };

    // always load EN first
    loadTranslations("en");

    // load selected language if not EN
    if (language !== "en") {
      loadTranslations(language);
    }
  }, [language, translations]);

  const messages = useMemo(() => {
    const current = translations[language] || {};
    const fallback = translations.en || {};

    return new Proxy(current, {
      get(target, prop) {
        if (prop in target) return target[prop];
        return fallback[prop]; // EN always exists
      },
    });
  }, [language, translations]);

  return (
    <TranslatorContext.Provider
      value={{ language, setLanguage, messages }}
    >
      {children}
    </TranslatorContext.Provider>
  );
};

export const useTranslator = () => {
  const context = useContext(TranslatorContext);
  if (!context) {
    throw new Error("useTranslator must be used within a TranslatorProvider");
  }
  return context;
};
